import { ReactNode, isValidElement } from 'react';
import {
  Article,
  FAQPage,
  Person,
  Question,
  VideoObject,
  WebPage,
} from 'schema-dts';

import { generalConfig } from '@sola/config/lib/getters.ts';
import { PageResponseDto } from '@sola/schema/lib/contentful.ts';
import { ArticleProps, FundProps } from '@sola/schema/lib/schema.ts';
import { toIsoString } from '@sola/utils/date.ts';

import { getOptimizedImageUrl } from './image-utils.ts';

const ensureAbsoluteUrl = (url?: string | null): string | undefined => {
  if (!url || url.trim() === '') {
    return undefined;
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `${generalConfig.domain.compassion}${url.startsWith('/') ? url : `/${url}`}`;
};

const getSchemaImageUrl = (
  imageUrl?: string | null,
  width = 1200,
  quality = 80,
): string | undefined => {
  if (!imageUrl || imageUrl.trim() === '') {
    return undefined;
  }

  const optimizedUrl = getOptimizedImageUrl(imageUrl, width, quality);
  return ensureAbsoluteUrl(optimizedUrl ?? imageUrl);
};

export type BuildVideoInput = {
  name?: string | null;
  description?: string | null;
  thumbnailUrl?: string | string[] | null;
  contentUrl?: string | null;
  embedUrl?: string | null;
  duration?: string | null;
  uploadDate?: string | null;
};

type WebPageInput = {
  name?: string | null;
  description?: string | null;
  url?: string | null;
  inLanguage?: string | string[] | null;
  datePublished?: string | Date | null;
  image?: string | null;
};

export type FaqItemInput = {
  question?: string | null;
  answer?: ReactNode | null;
};

const isEmptyString = (v: unknown): v is '' =>
  typeof v === 'string' && v.trim() === '';

export const cleanObject = <T extends Record<string, unknown>>(obj: T): T => {
  const result = Object.entries(obj).reduce<Record<string, unknown>>(
    (acc, [k, v]) => {
      if (v === undefined || v === null || isEmptyString(v)) {
        return acc;
      }

      if (Array.isArray(v)) {
        const arr = v
          .map((it) =>
            it !== null && typeof it === 'object' && !(it instanceof Date)
              ? cleanObject(it as Record<string, unknown>)
              : it,
          )
          .filter(
            (it) => !(it === undefined || it === null || isEmptyString(it)),
          );
        if (arr.length) {
          acc[k] = arr;
        }
        return acc;
      }

      if (typeof v === 'object' && !(v instanceof Date)) {
        const o = cleanObject(v as Record<string, unknown>);
        if (Object.keys(o).length) {
          acc[k] = o;
        }
        return acc;
      }

      acc[k] = v as unknown;
      return acc;
    },
    {},
  );
  return result as T;
};

export const reactNodeToText = (node: ReactNode): string => {
  if (node == null || node === false) {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(reactNodeToText).join(' ');
  }
  if (isValidElement(node)) {
    // @TODO: Tidy this up.
    return isValidElement(node)
      ? reactNodeToText(
          typeof node === 'object' &&
            node !== null &&
            'props' in node &&
            typeof node.props === 'object' &&
            node.props !== null &&
            'children' in node.props
            ? (node.props.children as ReactNode)
            : undefined,
        )
      : '';
  }
  return '';
};

export const buildVideoJsonLd = (
  input: BuildVideoInput,
): Partial<VideoObject> => {
  const rawThumbnailUrl =
    typeof input.thumbnailUrl === 'string'
      ? [input.thumbnailUrl]
      : (input.thumbnailUrl ?? undefined);

  const thumbnailUrl = rawThumbnailUrl
    ?.map((url) => getSchemaImageUrl(url, 1200, 75))
    .filter((url): url is string => url !== undefined);

  const uploadDate =
    input.uploadDate != null ? toIsoString(input.uploadDate) : undefined;

  const timeRequired =
    input.duration && input.duration.trim() !== '' ? input.duration : undefined;

  return cleanObject<Partial<VideoObject>>({
    name: input.name ?? undefined,
    description: input.description ?? undefined,
    thumbnailUrl: thumbnailUrl?.length ? thumbnailUrl : undefined,
    contentUrl: ensureAbsoluteUrl(input.contentUrl),
    embedUrl: ensureAbsoluteUrl(input.embedUrl),
    duration: input.duration ?? undefined,
    timeRequired,
    uploadDate,
  });
};

export const buildArticleJsonLd = (
  article: ArticleProps,
  path: string,
): Partial<Article> => {
  const primaryImageUrl =
    article.primaryMedia?.items?.[0]?.media?.image?.url ??
    article.primaryMedia?.items?.[0]?.mediaLogo?.image?.url ??
    generalConfig.publisher.logoUrl;

  const first = article.contributors?.[0];

  const authorImageUrl = getSchemaImageUrl(first?.image?.url, 400, 80);

  const author = first
    ? {
        '@type': 'Person',
        name: first.name,
        url: generalConfig.publisher.url,
        image: authorImageUrl
          ? { '@type': 'ImageObject', url: authorImageUrl }
          : undefined,
      }
    : {
        '@type': 'Organization',
        name: 'Compassion Team',
        url: generalConfig.publisher.url,
      };

  const optimizedPrimaryImageUrl = getSchemaImageUrl(primaryImageUrl, 1200, 80);

  return cleanObject<Partial<Article>>({
    headline: article.title,
    description: article.summary,
    datePublished: toIsoString(article.publishDate),
    dateModified: toIsoString(article.modifiedDate),

    author: author as unknown as Article['author'],
    mainEntityOfPage: { '@type': 'WebPage' },

    publisher: {
      '@type': 'Organization',
      name: generalConfig.publisher.name,
      logo: {
        '@type': 'ImageObject',
        url: ensureAbsoluteUrl(generalConfig.publisher.logoUrl),
      },
    } as unknown as Article['publisher'],

    image: optimizedPrimaryImageUrl ?? ensureAbsoluteUrl(primaryImageUrl),

    url: `${generalConfig.domain.compassion}${path}`,
  });
};

export const buildWebPageJsonLd = (input?: WebPageInput): Partial<WebPage> => {
  const datePublished =
    input?.datePublished instanceof Date
      ? input.datePublished.toISOString()
      : (input?.datePublished ?? undefined);

  const inLanguage = Array.isArray(input?.inLanguage)
    ? input.inLanguage.filter(Boolean).join(', ')
    : (input?.inLanguage ?? undefined);

  const url =
    input?.url ??
    (typeof window !== 'undefined' ? window.location.href : undefined);

  const optimizedImageUrl = getSchemaImageUrl(input?.image, 1200, 80);

  return cleanObject<Partial<WebPage>>({
    name: input?.name ?? undefined,
    description: input?.description ?? undefined,
    url,
    inLanguage,
    datePublished,
    image: optimizedImageUrl ?? ensureAbsoluteUrl(input?.image),
  });
};

export const buildWebPageJsonLdFromPage = (
  data: PageResponseDto,
): Partial<WebPage> => {
  const optimizedShareImageUrl = getSchemaImageUrl(
    data.shareImage?.image?.url,
    1200,
    80,
  );

  return cleanObject<Partial<WebPage>>({
    mainEntity: {
      '@type': 'Person',
      name: data.pageTitle ?? data.seoTitle ?? data.slug ?? undefined,
      url: data.path ?? undefined,
      image:
        optimizedShareImageUrl ??
        ensureAbsoluteUrl(data.shareImage?.image?.url),
      description:
        data.seoMetaDescription ??
        data.summary ??
        data.shareDescription ??
        undefined,
    } as Person,
  });
};

export const buildFaqPageJsonLd = (items: FaqItemInput[]): Partial<FAQPage> => {
  const questions: Question[] = items
    .filter((i) => (i.question ?? '').toString().trim() && i.answer != null)
    .map((i) => ({
      '@type': 'Question',
      name: (i.question ?? '').toString().trim(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: reactNodeToText(i.answer!).trim() || 'No answer provided.',
      },
    }));

  return questions.length
    ? { mainEntity: questions as FAQPage['mainEntity'] }
    : {};
};

export const buildHomeWebPageJsonLd = (): object => {
  const optimizedLogoUrl =
    getSchemaImageUrl(generalConfig.publisher.logoUrl, 600, 85) ??
    ensureAbsoluteUrl(generalConfig.publisher.logoUrl);
  const optimizedHomepageImageUrl =
    getSchemaImageUrl(generalConfig.images.homepage, 1200, 80) ??
    ensureAbsoluteUrl(generalConfig.images.homepage);

  return {
    '@id': `${generalConfig.domain.compassion}/#Organization`,
    name: generalConfig.publisher.name,
    alternateName: ['Compassion'],
    url: generalConfig.publisher.url,
    description:
      "Through sponsorship, you can release a child from poverty in Jesus' name. Help us put a stop to child poverty by empowering local churches to care for children in need.",
    logo: {
      '@type': 'ImageObject',
      url: optimizedLogoUrl,
      contentUrl: optimizedLogoUrl,
      name: `${generalConfig.publisher.name} - Logo`,
    },
    image: {
      '@type': 'ImageObject',
      url: optimizedHomepageImageUrl,
      contentUrl: optimizedHomepageImageUrl,
      name: generalConfig.publisher.name,
    },
    telephone: generalConfig.contact.telephone,
    sameAs: Object.values(generalConfig.socials),
    areaServed: generalConfig.contact.areaServed,
    address: {
      '@type': 'PostalAddress',
      ...generalConfig.address,
    },
  };
};

export const buildFundSchema = (fund: Partial<FundProps>): object => ({
  agent: {
    '@type': 'Organization',
    name: generalConfig.publisher.name,
    url: generalConfig.domain.compassion,
  },
  recipient: {
    '@type': 'Organization',
    name: fund?.fundTitle || generalConfig.publisher.name,
    url: generalConfig.domain.compassion,
    sameAs: Object.values(generalConfig.socials),
  },
  object: {
    '@type': 'MonetaryAmount',
    minValue:
      fund?.minOneTimeAmount ||
      fund?.suggestedAmount ||
      fund?.suggestedAmountsOneTime?.[0].value ||
      generalConfig.donation.amountMax,
    maxValue:
      fund?.donationLimitTotal ||
      fund?.suggestedAmountsOneTime?.at(-1)?.value ||
      generalConfig.donation.amountMax,
    currency: 'USD',
  },
});

export const buildGiftCatalogSchema = (): object => ({
  agent: {
    '@type': 'Organization',
    name: generalConfig.publisher.name,
  },
  recipient: {
    '@type': 'Organization',
    name: generalConfig.publisher.name,
  },
  target: {
    '@type': 'EntryPoint',
    url: generalConfig.domain.compassion,
  },
});
